import { useState, useEffect, useMemo } from "react";
import apiClient from "../../api/axios";
import {
  ExtractedContract,
  OwnerData,
  BuildingData,
  RoomData,
  OwnersResponse,
  BuildingsResponse,
  RoomsResponse,
} from "../../types/contract.d";
import { useFilterState } from "../../hooks/useFilterState";
import { useContractFilterOptions } from "../../hooks/useContractFilterOptions";

export type NormalizedDataItem = OwnerData | BuildingData | RoomData | ExtractedContract;

// --- カラム定義 ---
const itandiColumnDefs: { key: keyof ExtractedContract; label: string }[] = [
  { key: "property_name", label: "物件名" },
  { key: "room_name", label: "部屋名" },
  { key: "lessee_name", label: "入居者名" },
  { key: "document_type", label: "ドキュメント種別" },
  { key: "contractor_name", label: "契約者名" },
  { key: "contractor_gender", label: "契約者性別" },
  { key: "contractor_work_info", label: "契約者勤務先" },
  { key: "co_occupant_info", label: "同居人情報" },
  { key: "emergency_contact_info", label: "緊急連絡先" },
  { key: "property_address", label: "物件住所" },
  { key: "property_postal_code", label: "郵便番号" },
  { key: "rent", label: "賃料" },
  { key: "management_fee", label: "管理費" },
  { key: "contract_start_date", label: "契約開始日" },
  { key: "contract_end_date", label: "契約終了日" },
  { key: "renewal_fee_basis", label: "更新料(基準)" },
  { key: "renewal_fee_value", label: "更新料(値)" },
  { key: "renewal_fee_period", label: "更新料(期間)" },
  { key: "renewal_admin_fee", label: "更新事務手数料" },
  { key: "guarantee_company_name", label: "保証会社名" },
  { key: "guarantee_number", label: "保証番号" },
  { key: "insurance_company_info", label: "保険会社情報" },
  { key: "joint_guarantor_info", label: "連帯保証人情報" },
  { key: "owner_name", label: "貸主名" },
  { key: "owner_type", label: "貸主種別" },
  { key: "owner_company_name", label: "貸主会社名" },
  { key: "pet_info", label: "ペット情報" },
  { key: "vehicle_info", label: "車両情報" },
  { key: "key_info", label: "鍵情報" },
];

const landlordColumnDefs: { key: string; label: string }[] = [
  { key: "id", label: "家主識別NO" },
  { key: "name", label: "名称" },
  { key: "name_kana", label: "フリガナ" },
  { key: "postal_code", label: "郵便番号" },
  { key: "address1", label: "住所1" },
  { key: "address2", label: "住所2" },
  { key: "home_phone", label: "自宅Tel" },
  { key: "mobile_phone", label: "携帯電話番号" },
  { key: "bank_1_branch_code", label: "銀行支店コード1" },
  { key: "bank_1_account_type", label: "区分1" },
  { key: "bank_1_account_number", label: "番号1" },
  { key: "bank_1_account_holder_name", label: "名義1" },
  { key: "bank_2_branch_code", label: "銀行支店コード2" },
  { key: "bank_2_account_type", label: "区分2" },
  { key: "bank_2_account_number", label: "番号2" },
  { key: "bank_2_account_holder_name", label: "名義2" },
  { key: "legacy_id", label: "既存ID" },
];

const managedBuildingColumnDefs: { key: string; label: string }[] = [
  { key: "id", label: "NO" },
  { key: "building_code", label: "コード" },
  { key: "name_kana", label: "フリガナ" },
  { key: "name", label: "名称" },
  { key: "postal_code", label: "郵便番号" },
  { key: "address1", label: "住所1" },
  { key: "address2", label: "住所2" },
  { key: "owner_id", label: "家主識別NO" },
];

const roomColumnDefs: { key: string; label: string }[] = [
  { key: "id", label: "No" },
  { key: "building_id", label: "管理物件コード" },
  { key: "property_code", label: "物件コード" },
  { key: "room_number", label: "部屋番号" },
];

interface AllDataCtx {
  contracts: ExtractedContract[];
  loading: boolean;
  error: string | null;
  fetchData: () => Promise<void>;
  dataMode: 'full' | 'paginated' | null;
  sortOrder: 'asc' | 'desc';
  searchData: (query: string) => Promise<void>;
  clearSearch: () => Promise<void>;
  mode: 'browse' | 'search';
}

export function useExportData(format: string, allDataCtx: AllDataCtx) {
  const {
    contracts: itandiContracts,
    loading: itandiLoading,
    error: itandiError,
    fetchData: fetchItandiData,
    sortOrder,
    searchData,
    clearSearch,
    mode,
  } = allDataCtx;

  const [normalizedData, setNormalizedData] = useState<NormalizedDataItem[]>([]);
  const [normalizedLoading, setNormalizedLoading] = useState(false);
  const [normalizedError, setNormalizedError] = useState<string | null>(null);

  const filterState = useFilterState({ searchData, clearSearch });

  const columnDefs = useMemo(() => {
    switch (format) {
      case "itandi": return itandiColumnDefs as { key: string; label: string }[];
      case "landlord": return landlordColumnDefs;
      case "managed_building": return managedBuildingColumnDefs;
      case "room": return roomColumnDefs;
      default: return itandiColumnDefs as { key: string; label: string }[];
    }
  }, [format]);

  // itandi形式のデータ取得
  useEffect(() => {
    if (format === 'itandi' && itandiContracts.length === 0) {
      fetchItandiData();
    }
  }, [format, itandiContracts, fetchItandiData]);

  // 他3形式のデータ取得
  useEffect(() => {
    const fetchNormalizedData = async () => {
      if (format === 'itandi') return;
      setNormalizedLoading(true);
      setNormalizedError(null);
      try {
        let response;
        switch (format) {
          case 'landlord':
            response = await apiClient.get<OwnersResponse>('/api/owners/');
            setNormalizedData(response.data.owners);
            break;
          case 'managed_building':
            response = await apiClient.get<BuildingsResponse>('/api/buildings/');
            setNormalizedData(response.data.buildings);
            break;
          case 'room':
            response = await apiClient.get<RoomsResponse>('/api/rooms/');
            setNormalizedData(response.data.rooms);
            break;
        }
      } catch (err) {
        console.error(`Failed to fetch ${format} data:`, err);
        setNormalizedError(`${format}データの取得に失敗しました。`);
      } finally {
        setNormalizedLoading(false);
      }
    };
    fetchNormalizedData();
  }, [format]);

  const allData = useMemo(() => {
    const data = format === 'itandi' ? itandiContracts : normalizedData;
    if (format === 'itandi') {
      return [...(data as ExtractedContract[])].sort((a, b) => {
        const aId = a.id ?? 0;
        const bId = b.id ?? 0;
        return sortOrder === 'asc' ? aId - bId : bId - aId;
      });
    }
    return data;
  }, [format, itandiContracts, normalizedData, sortOrder]);

  // itandi形式のみフィルター計算を行う。他形式は空配列を渡してスキップ。
  const contractsForFiltering = format === 'itandi' ? (allData as ExtractedContract[]) : [];
  const filterOptions = useContractFilterOptions(
    contractsForFiltering,
    filterState.propertyFilter,
    filterState.roomFilter,
    filterState.lesseeFilter
  );

  // フィルタリング結果: itandi形式は filteredContracts、他形式は allData をそのまま使う
  const results: NormalizedDataItem[] = format === 'itandi'
    ? filterOptions.filteredContracts
    : allData;

  const loading = format === 'itandi' ? itandiLoading : normalizedLoading;
  const contextError = format === 'itandi' ? itandiError : normalizedError;

  return {
    results,
    allData,
    columnDefs,
    propertyFilter: filterState.propertyFilter,
    setPropertyFilter: filterState.setPropertyFilter,
    roomFilter: filterState.roomFilter,
    setRoomFilter: filterState.setRoomFilter,
    lesseeFilter: filterState.lesseeFilter,
    setLesseeFilter: filterState.setLesseeFilter,
    propertyOptions: filterOptions.propertyOptions,
    roomOptions: filterOptions.roomOptions,
    lesseeOptions: filterOptions.lesseeOptions,
    loading,
    contextError,
    handleSearch: filterState.handleSearch,
    handleClearSearch: filterState.handleClearSearch,
    mode,
    itandiLoading,
  };
}
